import React, { useState, useEffect } from 'react';
import { useAdminAuth } from '../../hooks/useAdminAuth';
import { 
  getAllWithdrawals, 
  approveWithdrawal, 
  rejectWithdrawal,
  WITHDRAWAL_STATUS,
  PAYMENT_NETWORKS
} from '../../services/withdrawalService';
import { getNetworkDisplayName } from '../../utils/walletValidation';
import { verifyAdminAccess } from '../../services/adminService';
import { auth } from '../../firebase/config';

function WithdrawalManagement() {
  const { isAdmin } = useAdminAuth();
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [transactionHash, setTransactionHash] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [actionError, setActionError] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isAdmin) {
      loadWithdrawals();
      
      // Refresh every 30 seconds
      const interval = setInterval(loadWithdrawals, 30000);
      return () => clearInterval(interval);
    }
  }, [isAdmin, filterStatus]);

  const loadWithdrawals = async () => {
    try {
      setLoading(true);
      setError('');
      
      const filters = filterStatus !== 'all' ? { status: filterStatus } : {};
      const data = await getAllWithdrawals(filters);
      setWithdrawals(data);
    } catch (err) {
      console.error('Error loading withdrawals:', err);
      setError(err.message || 'Failed to load withdrawals');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!transactionHash.trim()) {
      setActionError('Transaction hash is required');
      return;
    }

    setProcessing(true);
    setActionError('');

    try {
      const adminInfo = await verifyAdminAccess();
      await approveWithdrawal(
        selectedWithdrawal.id,
        transactionHash.trim(),
        adminInfo.adminId,
        adminInfo.adminEmail
      );

      // Reload withdrawals
      await loadWithdrawals();
      setShowApproveModal(false);
      setSelectedWithdrawal(null);
      setTransactionHash('');
    } catch (err) {
      setActionError(err.message || 'Failed to approve withdrawal');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      setActionError('Rejection reason is required');
      return;
    }

    setProcessing(true);
    setActionError('');

    try {
      const adminInfo = await verifyAdminAccess();
      await rejectWithdrawal(
        selectedWithdrawal.id,
        rejectionReason.trim(),
        adminInfo.adminId,
        adminInfo.adminEmail
      );

      // Reload withdrawals
      await loadWithdrawals();
      setShowRejectModal(false);
      setSelectedWithdrawal(null);
      setRejectionReason('');
    } catch (err) {
      setActionError(err.message || 'Failed to reject withdrawal');
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      [WITHDRAWAL_STATUS.UNDER_REVIEW]: { class: 'warning', icon: 'bi-clock-history' },
      [WITHDRAWAL_STATUS.APPROVED]: { class: 'success', icon: 'bi-check-circle' },
      [WITHDRAWAL_STATUS.REJECTED]: { class: 'danger', icon: 'bi-x-circle' }
    };
    const config = statusConfig[status] || { class: 'secondary', icon: 'bi-circle' };
    return (
      <span className={`badge bg-${config.class}`}>
        <i className={`bi ${config.icon} me-1`}></i>
        {status}
      </span>
    );
  };

  const getStatusCounts = () => {
    const counts = {
      all: withdrawals.length,
      [WITHDRAWAL_STATUS.UNDER_REVIEW]: 0,
      [WITHDRAWAL_STATUS.APPROVED]: 0,
      [WITHDRAWAL_STATUS.REJECTED]: 0
    };

    withdrawals.forEach(w => {
      if (w.status === WITHDRAWAL_STATUS.UNDER_REVIEW) counts[WITHDRAWAL_STATUS.UNDER_REVIEW]++;
      else if (w.status === WITHDRAWAL_STATUS.APPROVED) counts[WITHDRAWAL_STATUS.APPROVED]++;
      else if (w.status === WITHDRAWAL_STATUS.REJECTED) counts[WITHDRAWAL_STATUS.REJECTED]++;
    });

    return counts;
  };

  if (!isAdmin) {
    return (
      <div className="alert alert-danger">
        <i className="bi bi-exclamation-triangle me-2"></i>
        Unauthorized: Admin access required
      </div>
    );
  }

  const statusCounts = getStatusCounts();

  return (
    <div className="withdrawal-management">
      {/* Header - Responsive */}
      <div 
        className="mb-3 mb-md-4 p-3 p-md-4 rounded-3 text-white"
        style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)'
        }}
      >
        <div className="d-flex justify-content-between align-items-start flex-column flex-md-row gap-3">
          <div>
            <h2 className="mb-1" style={{ fontWeight: '600', fontSize: isMobile ? '1.25rem' : '1.75rem' }}>
              <i className="bi bi-wallet2 me-2"></i>
              <span className="d-none d-sm-inline">Withdrawal Management</span>
              <span className="d-sm-none">Withdrawals</span>
            </h2>
            <p className="mb-0 opacity-75" style={{ fontSize: isMobile ? '0.85rem' : '1rem' }}>
              Review and process withdrawal requests
            </p>
          </div>
          <button 
            className="btn btn-light"
            onClick={loadWithdrawals}
            disabled={loading}
            style={{
              minWidth: '44px',
              minHeight: '44px',
              touchAction: 'manipulation'
            }}
          >
            <i className="bi bi-arrow-clockwise me-md-2"></i>
            <span className="d-none d-md-inline">Refresh</span>
          </button>
        </div>
      </div>

      {/* Status Filter Tabs - Responsive */}
      <div className="card mb-3 mb-md-4">
        <div className="card-body p-2 p-md-3">
          <div className="d-flex gap-1 gap-md-2 flex-wrap">
            <button
              className={`btn ${filterStatus === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setFilterStatus('all')}
              style={{
                minHeight: '44px',
                fontSize: isMobile ? '0.85rem' : '0.875rem',
                padding: isMobile ? '0.5rem 0.75rem' : '0.5rem 1rem',
                touchAction: 'manipulation',
                flex: isMobile ? '1 1 calc(50% - 0.25rem)' : '0 0 auto'
              }}
            >
              All ({statusCounts.all})
            </button>
            <button
              className={`btn ${filterStatus === WITHDRAWAL_STATUS.UNDER_REVIEW ? 'btn-warning' : 'btn-outline-warning'}`}
              onClick={() => setFilterStatus(WITHDRAWAL_STATUS.UNDER_REVIEW)}
              style={{
                minHeight: '44px',
                fontSize: isMobile ? '0.85rem' : '0.875rem',
                padding: isMobile ? '0.5rem 0.75rem' : '0.5rem 1rem',
                touchAction: 'manipulation',
                flex: isMobile ? '1 1 calc(50% - 0.25rem)' : '0 0 auto'
              }}
            >
              <span className="d-none d-sm-inline">Under Review </span>
              <span className="d-sm-none">Review </span>
              ({statusCounts[WITHDRAWAL_STATUS.UNDER_REVIEW]})
            </button>
            <button
              className={`btn ${filterStatus === WITHDRAWAL_STATUS.APPROVED ? 'btn-success' : 'btn-outline-success'}`}
              onClick={() => setFilterStatus(WITHDRAWAL_STATUS.APPROVED)}
              style={{
                minHeight: '44px',
                fontSize: isMobile ? '0.85rem' : '0.875rem',
                padding: isMobile ? '0.5rem 0.75rem' : '0.5rem 1rem',
                touchAction: 'manipulation',
                flex: isMobile ? '1 1 calc(50% - 0.25rem)' : '0 0 auto'
              }}
            >
              Approved ({statusCounts[WITHDRAWAL_STATUS.APPROVED]})
            </button>
            <button
              className={`btn ${filterStatus === WITHDRAWAL_STATUS.REJECTED ? 'btn-danger' : 'btn-outline-danger'}`}
              onClick={() => setFilterStatus(WITHDRAWAL_STATUS.REJECTED)}
              style={{
                minHeight: '44px',
                fontSize: isMobile ? '0.85rem' : '0.875rem',
                padding: isMobile ? '0.5rem 0.75rem' : '0.5rem 1rem',
                touchAction: 'manipulation',
                flex: isMobile ? '1 1 calc(50% - 0.25rem)' : '0 0 auto'
              }}
            >
              Rejected ({statusCounts[WITHDRAWAL_STATUS.REJECTED]})
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
          <button 
            type="button" 
            className="btn-close" 
            onClick={() => setError('')}
            aria-label="Close"
          ></button>
        </div>
      )}

      {/* Withdrawals Table */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : withdrawals.length === 0 ? (
        <div className="card">
          <div className="card-body text-center py-5">
            <i className="bi bi-inbox" style={{ fontSize: '3rem', color: '#ccc' }}></i>
            <p className="text-muted mt-3">No withdrawals found</p>
          </div>
        </div>
      ) : isMobile ? (
        // Mobile Card View
        <div className="row g-3">
          {withdrawals.map((withdrawal) => (
            <div key={withdrawal.id} className="col-12">
              <div className="card">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div>
                      <strong className="d-block">{withdrawal.username || 'N/A'}</strong>
                      <small className="text-muted">{withdrawal.userEmail}</small>
                    </div>
                    {getStatusBadge(withdrawal.status)}
                  </div>
                  
                  <div className="mb-2">
                    <strong>{withdrawal.amount} USDT</strong>
                    <span className="badge bg-info ms-2">
                      {getNetworkDisplayName(withdrawal.paymentNetwork)}
                    </span>
                  </div>
                  
                  <div className="mb-2">
                    <small className="text-muted d-block mb-1">Wallet Address:</small>
                    <code 
                      style={{ 
                        fontSize: '0.8rem',
                        wordBreak: 'break-all',
                        display: 'block',
                        backgroundColor: '#f8f9fa',
                        padding: '0.5rem',
                        borderRadius: '0.25rem'
                      }}
                    >
                      {withdrawal.walletAddress}
                    </code>
                  </div>
                  
                  <div className="mb-2">
                    <small className="text-muted">Created: {formatDate(withdrawal.createdAt)}</small>
                  </div>
                  
                  {withdrawal.status === WITHDRAWAL_STATUS.UNDER_REVIEW && (
                    <div className="d-grid gap-2 mt-3">
                      <button
                        className="btn btn-success"
                        onClick={() => {
                          setSelectedWithdrawal(withdrawal);
                          setShowApproveModal(true);
                          setTransactionHash('');
                          setActionError('');
                        }}
                        style={{
                          minHeight: '44px',
                          touchAction: 'manipulation'
                        }}
                      >
                        <i className="bi bi-check-circle me-2"></i>
                        Approve
                      </button>
                      <button
                        className="btn btn-danger"
                        onClick={() => {
                          setSelectedWithdrawal(withdrawal);
                          setShowRejectModal(true);
                          setRejectionReason('');
                          setActionError('');
                        }}
                        style={{
                          minHeight: '44px',
                          touchAction: 'manipulation'
                        }}
                      >
                        <i className="bi bi-x-circle me-2"></i>
                        Reject
                      </button>
                    </div>
                  )}
                  
                  {withdrawal.status === WITHDRAWAL_STATUS.APPROVED && withdrawal.transactionHash && (
                    <div className="mt-2">
                      <small className="text-muted d-block mb-1">Transaction Hash:</small>
                      <code style={{ fontSize: '0.75rem', wordBreak: 'break-all' }}>
                        {withdrawal.transactionHash}
                      </code>
                    </div>
                  )}
                  
                  {withdrawal.status === WITHDRAWAL_STATUS.REJECTED && (
                    <div className="alert alert-danger py-2 mt-2 mb-0">
                      <small><strong>Reason:</strong> {withdrawal.rejectionReason}</small>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        // Desktop Table View
        <div className="card">
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th>ID</th>
                    <th>User</th>
                    <th>Amount</th>
                    <th>Network</th>
                    <th>Wallet Address</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {withdrawals.map((withdrawal) => (
                    <tr key={withdrawal.id}>
                      <td>
                        <code style={{ fontSize: '0.85rem' }}>
                          {withdrawal.id.substring(0, 8)}...
                        </code>
                      </td>
                      <td>
                        <div>
                          <strong>{withdrawal.username || 'N/A'}</strong>
                          <br />
                          <small className="text-muted">{withdrawal.userEmail}</small>
                        </div>
                      </td>
                      <td>
                        <strong>{withdrawal.amount} USDT</strong>
                      </td>
                      <td>
                        <span className="badge bg-info">
                          {getNetworkDisplayName(withdrawal.paymentNetwork)}
                        </span>
                      </td>
                      <td>
                        <code 
                          style={{ 
                            fontSize: '0.85rem',
                            wordBreak: 'break-all',
                            display: 'block',
                            maxWidth: '300px'
                          }}
                          title={withdrawal.walletAddress}
                        >
                          {withdrawal.walletAddress}
                        </code>
                      </td>
                      <td>{getStatusBadge(withdrawal.status)}</td>
                      <td>
                        <small>{formatDate(withdrawal.createdAt)}</small>
                      </td>
                      <td>
                        {withdrawal.status === WITHDRAWAL_STATUS.UNDER_REVIEW && (
                          <div className="btn-group btn-group-sm">
                            <button
                              className="btn btn-success"
                              onClick={() => {
                                setSelectedWithdrawal(withdrawal);
                                setShowApproveModal(true);
                                setTransactionHash('');
                                setActionError('');
                              }}
                              style={{ minHeight: '36px', touchAction: 'manipulation' }}
                            >
                              <i className="bi bi-check-circle me-1"></i>
                              Approve
                            </button>
                            <button
                              className="btn btn-danger"
                              onClick={() => {
                                setSelectedWithdrawal(withdrawal);
                                setShowRejectModal(true);
                                setRejectionReason('');
                                setActionError('');
                              }}
                              style={{ minHeight: '36px', touchAction: 'manipulation' }}
                            >
                              <i className="bi bi-x-circle me-1"></i>
                              Reject
                            </button>
                          </div>
                        )}
                        {withdrawal.status === WITHDRAWAL_STATUS.APPROVED && withdrawal.transactionHash && (
                          <div>
                            <small className="text-muted">Hash:</small>
                            <br />
                            <code style={{ fontSize: '0.7rem', wordBreak: 'break-all' }}>
                              {withdrawal.transactionHash.substring(0, 15)}...
                            </code>
                          </div>
                        )}
                        {withdrawal.status === WITHDRAWAL_STATUS.REJECTED && (
                          <div>
                            <small className="text-danger">
                              <strong>Reason:</strong> {withdrawal.rejectionReason}
                            </small>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Approve Modal - Responsive */}
      {showApproveModal && selectedWithdrawal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
          <div className={`modal-dialog modal-dialog-centered ${isMobile ? 'modal-dialog-scrollable' : ''}`} style={{ margin: isMobile ? '1rem' : 'auto' }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Approve Withdrawal</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowApproveModal(false);
                    setSelectedWithdrawal(null);
                    setTransactionHash('');
                    setActionError('');
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <strong>User:</strong> {selectedWithdrawal.username} ({selectedWithdrawal.userEmail})
                </div>
                <div className="mb-3">
                  <strong>Amount:</strong> {selectedWithdrawal.amount} USDT
                </div>
                <div className="mb-3">
                  <strong>Network:</strong> {getNetworkDisplayName(selectedWithdrawal.paymentNetwork)}
                </div>
                <div className="mb-3">
                  <strong>Wallet Address:</strong>
                  <br />
                  <code>{selectedWithdrawal.walletAddress}</code>
                </div>

                {actionError && (
                  <div className="alert alert-danger">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    {actionError}
                  </div>
                )}

                <div className="mb-3">
                  <label htmlFor="transactionHash" className="form-label">
                    Transaction Hash <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="transactionHash"
                    value={transactionHash}
                    onChange={(e) => {
                      setTransactionHash(e.target.value);
                      setActionError('');
                    }}
                    placeholder="Enter blockchain transaction hash"
                    required
                    disabled={processing}
                  />
                  <small className="text-muted">
                    Enter the transaction hash from the blockchain after processing the withdrawal
                  </small>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowApproveModal(false);
                    setSelectedWithdrawal(null);
                    setTransactionHash('');
                    setActionError('');
                  }}
                  disabled={processing}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-success w-100 w-md-auto"
                  onClick={handleApprove}
                  disabled={processing || !transactionHash.trim()}
                  style={{ minHeight: '44px', touchAction: 'manipulation' }}
                >
                  {processing ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Processing...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-circle me-2"></i>
                      Approve Withdrawal
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal - Responsive */}
      {showRejectModal && selectedWithdrawal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
          <div className={`modal-dialog modal-dialog-centered ${isMobile ? 'modal-dialog-scrollable' : ''}`} style={{ margin: isMobile ? '1rem' : 'auto' }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Reject Withdrawal</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowRejectModal(false);
                    setSelectedWithdrawal(null);
                    setRejectionReason('');
                    setActionError('');
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <strong>User:</strong> {selectedWithdrawal.username} ({selectedWithdrawal.userEmail})
                </div>
                <div className="mb-3">
                  <strong>Amount:</strong> {selectedWithdrawal.amount} USDT
                </div>
                <div className="mb-3">
                  <strong>Network:</strong> {getNetworkDisplayName(selectedWithdrawal.paymentNetwork)}
                </div>
                <div className="mb-3">
                  <strong>Wallet Address:</strong>
                  <br />
                  <code style={{ wordBreak: 'break-all' }}>{selectedWithdrawal.walletAddress}</code>
                </div>

                {actionError && (
                  <div className="alert alert-danger">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    {actionError}
                  </div>
                )}

                <div className="mb-3">
                  <label htmlFor="rejectionReason" className="form-label">
                    Rejection Reason <span className="text-danger">*</span>
                  </label>
                  <textarea
                    className="form-control"
                    id="rejectionReason"
                    rows="4"
                    value={rejectionReason}
                    onChange={(e) => {
                      setRejectionReason(e.target.value);
                      setActionError('');
                    }}
                    placeholder="Enter reason for rejection (required)"
                    required
                    disabled={processing}
                  />
                  <small className="text-muted">
                    This reason will be shown to the user. The funds will be automatically refunded.
                  </small>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowRejectModal(false);
                    setSelectedWithdrawal(null);
                    setRejectionReason('');
                    setActionError('');
                  }}
                  disabled={processing}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger w-100 w-md-auto"
                  onClick={handleReject}
                  disabled={processing || !rejectionReason.trim()}
                  style={{ minHeight: '44px', touchAction: 'manipulation' }}
                >
                  {processing ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Processing...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-x-circle me-2"></i>
                      Reject Withdrawal
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default WithdrawalManagement;

